import React, { useEffect, useRef, useState } from "react";
import { api, apiErrorMessage, formatDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Upload, ImageSquare, FilePdf, FileDoc, FileXls, FileCsv, File as FileIcon,
    DownloadSimple, Trash, MagnifyingGlass, X,
} from "@phosphor-icons/react";

const ICON_MAP = {
    pdf: FilePdf, doc: FileDoc, docx: FileDoc, xls: FileXls, xlsx: FileXls,
    csv: FileCsv, txt: FileDoc, json: FileDoc, zip: FileIcon,
};

function iconFor(filename) {
    const ext = (filename || "").split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "heic"].includes(ext)) return ImageSquare;
    return ICON_MAP[ext] || FileIcon;
}

function formatBytes(n) {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(1)} ${units[i]}`;
}

export default function Files() {
    const { user } = useAuth();
    const canDelete = hasRole(user, "manager");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [search, setSearch] = useState("");
    const [kindFilter, setKindFilter] = useState("all");
    const fileInput = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (kindFilter !== "all") params.kind = kindFilter;
            if (search) params.search = search;
            const { data } = await api.get("/files", { params });
            setItems(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter, search]);

    const uploadFiles = async (fileList) => {
        setUploading(true);
        let success = 0;
        for (const f of fileList) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
                success++;
            } catch (e) { toast.error(`${f.name}: ${apiErrorMessage(e)}`); }
        }
        setUploading(false);
        if (success) toast.success(`Uploaded ${success} file${success !== 1 ? "s" : ""}`);
        load();
    };

    const remove = async (f) => {
        if (!window.confirm(`Delete "${f.original_filename}"? This will hide it from the library.`)) return;
        try {
            await api.delete(`/files/${f.id}`);
            toast.success("File deleted");
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) uploadFiles(files);
    };

    return (
        <div className="space-y-6" data-testid="files-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Media</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Files & Media</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{items.length} file{items.length !== 1 && "s"} · Upload documents, images and receipts up to 10 MB.</p>
                </div>
                <Button onClick={() => fileInput.current?.click()} disabled={uploading} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="files-upload-btn">
                    <Upload size={16} weight="bold" className="mr-1.5" /> {uploading ? "Uploading..." : "Upload Files"}
                </Button>
                <input ref={fileInput} type="file" multiple hidden onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="files-input" />
            </div>

            {/* Drag-drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                className={"border-2 border-dashed rounded-md py-8 text-center transition-colors " +
                    (dragActive ? "border-[#15803D] bg-green-50" : "border-neutral-300 bg-white")}
                data-testid="files-dropzone"
            >
                <Upload size={28} weight="duotone" className="mx-auto text-neutral-400" />
                <div className="text-sm font-semibold text-neutral-700 mt-2">Drop files here or click Upload</div>
                <div className="text-xs text-neutral-500 mt-1">Images (jpg/png/gif/webp) · Documents (pdf/docx/xlsx/csv/txt) · zip · up to 10 MB each</div>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input placeholder="Search by filename..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11" data-testid="files-search" />
                </div>
                <Select value={kindFilter} onValueChange={setKindFilter}>
                    <SelectTrigger className="w-full md:w-44 h-11" data-testid="files-kind-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="image">Images</SelectItem>
                        <SelectItem value="document">Documents</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500">Loading files...</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-md py-16 text-center bg-white" data-testid="files-empty">
                    <FileIcon size={40} weight="thin" className="mx-auto text-neutral-300" />
                    <div className="mt-3 text-lg font-bold font-display">No files yet</div>
                    <div className="text-sm text-neutral-500 mt-1">Upload your first file to build your media library.</div>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {items.map((f) => (
                        <FileTile key={f.id} file={f} onDelete={canDelete ? () => remove(f) : null} />
                    ))}
                </div>
            )}
        </div>
    );
}

function FileTile({ file, onDelete }) {
    const Icon = iconFor(file.original_filename);
    const isImage = file.kind === "image";
    const url = fileUrl(file.storage_path);
    return (
        <div className="bg-white border border-neutral-200 rounded-md overflow-hidden hover:border-neutral-400 transition-colors fade-up flex flex-col" data-testid={`file-tile-${file.id}`}>
            <div className="aspect-square bg-neutral-100 flex items-center justify-center overflow-hidden">
                {isImage ? (
                    <img src={url} alt={file.original_filename} className="w-full h-full object-cover" />
                ) : (
                    <Icon size={42} weight="duotone" color="#15803D" />
                )}
            </div>
            <div className="p-2.5 flex-1 flex flex-col">
                <div className="text-xs font-semibold text-neutral-800 line-clamp-2 leading-tight" title={file.original_filename}>
                    {file.original_filename}
                </div>
                <div className="mt-1 text-[10px] text-neutral-500 tabular">{formatBytes(file.size)} · {formatDate(file.created_at)}</div>
                <div className="mt-1 text-[10px] text-neutral-400 truncate" title={file.uploaded_by_name}>by {file.uploaded_by_name}</div>
                <div className="mt-2 pt-2 border-t border-neutral-100 flex items-center gap-1">
                    <a href={url} target="_blank" rel="noreferrer" download={file.original_filename}
                       className="flex items-center gap-1 text-[11px] font-semibold text-neutral-700 hover:text-neutral-900"
                       data-testid={`file-download-${file.id}`}>
                        <DownloadSimple size={12} weight="bold" /> Open
                    </a>
                    {onDelete && (
                        <button onClick={onDelete}
                                className="ml-auto text-red-500 hover:text-red-700"
                                data-testid={`file-delete-${file.id}`}
                                title="Delete">
                            <Trash size={12} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
